/* istanbul ignore file */ // ignore this file for component test as we cover it in selenium test
import React from 'react';
import PropTypes from 'prop-types';
import { compose, findWhere, result, defaults, has } from 'underscore';
import { withInstrumentation } from '@bingads-webui-react/hoc-with-instrumentation';
import { withScope, QueryParam } from '@bingads-webui-react/scope-service';
import { withSiteMap, siteMapPropType } from '@bingads-webui-campaign-react/with-site-map';
import {
  getFilteredCampaignTypeFromRawFilter,
  CampaignType,
} from '@bingads-webui-campaign-react/shell-ui-common';
import { withGlobalFilter } from '@bingads-webui-campaign-react/global-filter-context';
import { WaterfallFilterType } from '@bingads-webui-campaign/filter-storage';
import { withI18nConsumer } from '@bingads-webui-react/i18n-context';
import { EntityBreadcrumb } from '@bingads-webui-react/global-menu';
import { setClarityTag } from '@bingads-webui-universal/clarity';
import {
  convertToSummaryBarCampaign,
  convertToSummaryBarAdGroup,
  convertToSummaryBarExperimentCampaign,
} from './helpers/entity-converter';
import {
  campaignTypes,
  convertToBreadCrumbItems,
} from './helpers';
import {
  WithCampaignRepository,
  WithAdGroupRepository,
  WithExperimentRepository,
} from './repository';

class GlobalMenuEntityBarInternal extends React.Component {
  constructor(props) {
    super(props);

    this.campaignSummarybarPerfMarker = props.appPerfMarker.createChild('GlobalMenu_Entity_Bar');
    this.markAction('willInit');
    this.summaryBarActivityLogger = props.instrumentation.globalMenuEntityBarScenario.create();
  }

  componentDidMount() {
    this.markAction('done');
  }

  get getCampaignType() {
    return getFilteredCampaignTypeFromRawFilter(this.props[WaterfallFilterType.CampaignType]);
  }

  get getFinalProps() {
    const campaignType = this.getCampaignType;
    const props = {
      ...this.props,
      campaignType,
      summaryBarActivityLogger: this.summaryBarActivityLogger,
      setClarityTag: this.setClarityTag,
    };

    return props;
  }

  get getCampaignLoadFallback() {
    const props = this.getFinalProps;

    const items = convertToBreadCrumbItems(props);
    const currentCampaignType = findWhere(campaignTypes, { id: props.campaignType });
    const overflowAriaLabel = props.i18n.getString(result(currentCampaignType, 'i18nKey'));

    return (
      <EntityBreadcrumb
        items={items}
        overflowAriaLabel={overflowAriaLabel}
        maxDisplayedItems={3}
      />
    );
  }

  setClarityTag = () => {
    setClarityTag({ GlobalMenuEntityBar: 'true' });
  }

  markAction = (action) => {
    if (this.campaignSummarybarPerfMarker && action) {
      this.campaignSummarybarPerfMarker[action]();
    }
  }

  consumeOdataResults = RenderSummaryBar => ({
    adGroupFromOData,
    campaignFromOData,
    osData,
  }) => {
    this.markAction('willRender');

    const campaign = campaignFromOData
      ? convertToSummaryBarCampaign(campaignFromOData)
      : null;

    const adGroup = adGroupFromOData
      ? convertToSummaryBarAdGroup({
        ...adGroupFromOData,
        Status: adGroupFromOData.EntityStatus,
        Campaign: campaignFromOData,
      })
      : null;

    if (campaignFromOData && campaignFromOData.ExperimentId) {
      const experimentId = campaignFromOData.ExperimentId;
      const extendedCampaign = { experimentId, ...campaign };

      return (<WithExperimentRepository
        campaignLoadFallback={this.getCampaignLoadFallback}
        campaign={extendedCampaign}
        adGroup={adGroup}
        experimentId={experimentId}
        i18n={this.props.i18n}
        WrappedComponent={RenderSummaryBar}
      />);
    }

    return (<RenderSummaryBar campaign={campaign} adGroup={adGroup} osData={osData} />);
  };

  consumeAdGroupRepository = SummaryBarWithAdGroupCriterionResults => passThroughProps => (
    (
      <WithAdGroupRepository
        campaignLoadFallback={this.getCampaignLoadFallback}
        WrappedComponent={SummaryBarWithAdGroupCriterionResults}
        i18n={this.props.i18n}
        {...passThroughProps}
      />
    )
  );

  consumeCampaignRepository = SummaryBarWithCampaignCriterionResults => (
    (
      <WithCampaignRepository
        campaignLoadFallback={this.getCampaignLoadFallback}
        WrappedComponent={SummaryBarWithCampaignCriterionResults}
        i18n={this.props.i18n}
      />
    )
  );

  renderSummaryBar = () => ({
    experimentFromOData,
    campaign,
    adGroup: summaryBarAdGroup,
  }) => {
    const { i18n, scope, siteMap } = this.props;
    const summaryBarCampaign = experimentFromOData ?
      convertToSummaryBarExperimentCampaign(experimentFromOData, campaign)
      : campaign;

    const campaignType = this.getCampaignType;
    let breadcrumbsProps = { campaignType };
    if (summaryBarAdGroup) {
      const {
        id,
        name,
        campaignId,
        campaignName,
      } = summaryBarAdGroup;
      if (has(scope.values, 'adId')) {
        breadcrumbsProps = {
          campaignName,
          campaignId,
          campaignType: campaignType === CampaignType.Default ? campaignType : CampaignType.All,
          adGroupId: id,
          adGroupName: name,
          adName: i18n.getString(_TL_('Responsive search ad')),
        };
      } else {
        breadcrumbsProps = {
          campaignName,
          campaignId,
          campaignType,
          adGroupId: id,
          adGroupName: name,
        };
      }
    } else if (summaryBarCampaign) {
      const { id, name } = summaryBarCampaign;
      breadcrumbsProps = { campaignName: name, campaignId: id, campaignType };
    }

    const items = convertToBreadCrumbItems(defaults({
      i18n,
      scope,
      siteMap,
      summaryBarActivityLogger: this.summaryBarActivityLogger,
      setClarityTag: this.setClarityTag,
    }, breadcrumbsProps));
    const currentCampaignType = findWhere(campaignTypes, { id: campaignType });
    const overflowAriaLabel = i18n.getString(result(currentCampaignType, 'i18nKey'));

    return (
      <EntityBreadcrumb
        items={items}
        overflowAriaLabel={overflowAriaLabel}
        maxDisplayedItems={3}
      />
    );
  };

  render() {
    this.markAction('willFetchData');
    const renderSummaryBarWithData = compose(
      this.consumeCampaignRepository,
      this.consumeAdGroupRepository,
      this.consumeOdataResults,
      this.renderSummaryBar
    );

    return renderSummaryBarWithData();
  }
}

GlobalMenuEntityBarInternal.propTypes = {
  scope: PropTypes.instanceOf(QueryParam).isRequired,
  siteMap: siteMapPropType.isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  appPerfMarker: PropTypes.shape({
    createChild: PropTypes.func.isRequired,
  }),
  instrumentation: PropTypes.shape({
    globalMenuEntityBarScenario: PropTypes.any,
  }).isRequired,
};

GlobalMenuEntityBarInternal.defaultProps = {
  appPerfMarker: null,
};

export const GlobalMenuEntityBar
  = withInstrumentation(withSiteMap(withGlobalFilter(withScope(withI18nConsumer(GlobalMenuEntityBarInternal)))));
